import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  ClipboardCheck,
  Clock,
  User,
  Server,
  Package,
  ExternalLink,
  CheckCircle,
} from "lucide-react";
import { myApprovalsApi, ApprovalWithDetails } from "@/api/client";
import { titleCase } from "@/lib/utils";

export default function MyApprovals() {
  const {
    data: approvalsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["my-approvals"],
    queryFn: myApprovalsApi.listPending,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  const approvals = approvalsData?.approvals ?? [];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-gray-200 rounded" />
          <div className="h-24 bg-gray-200 rounded" />
          <div className="h-24 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">
            Failed to load approvals. Please try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
          <ClipboardCheck className="h-7 w-7 text-brand-600" />
          My Approvals
        </h1>
        <p className="mt-1 text-gray-600">
          Deployment requests awaiting your approval
        </p>
      </div>

      {/* Approvals List */}
      {approvals.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            All caught up!
          </h3>
          <p className="text-gray-600">
            You have no pending approvals at this time.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {approvals.map((approval: ApprovalWithDetails) => (
            <div
              key={approval.id}
              className="bg-white rounded-lg border border-gray-200 p-5 hover:border-brand-300 hover:shadow-sm transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Service and Version */}
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="h-5 w-5 text-brand-600" />
                    <span className="text-lg font-semibold text-gray-900">
                      {titleCase(approval.service_name)}
                    </span>
                    <span className="px-2 py-0.5 bg-brand-100 text-brand-700 text-sm font-medium rounded">
                      {approval.version}
                    </span>
                  </div>

                  {/* Environment */}
                  <div className="flex items-center gap-2 text-gray-600 mb-3">
                    <Server className="h-4 w-4" />
                    <span>{approval.environment_name}</span>
                  </div>

                  {/* Meta info */}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <User className="h-4 w-4" />
                      <span>
                        Requested by {approval.requested_by_name || "Unknown"}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{formatDate(approval.requested_at)}</span>
                    </div>
                    <div className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                      {approval.role_name || approval.role}
                    </div>
                  </div>
                </div>

                {/* Action Button */}
                <div className="ml-4">
                  <Link
                    to={`/approvals/${approval.id}/review`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
                  >
                    Review
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
